import { IMediaItem, IUploadMediaItem, MediaStatus } from 'types/media'
import { IAvatarData, IPublicAvatar } from 'store/types/aiAvatar'
import { IAIVideoProject } from 'types/aiVideo'
import {
  DEFAULT_MEDIA_DURATION,
  MIN_PREMIUM_QUALITY,
  PROJECT_MEDIA_TYPES
} from 'consts'
import { IDimensions } from 'types'
import { FIT } from 'consts/canvas'
import { calculateMediaRect, getLastMediaItemEndTime } from 'utils/media'
import { estimateAvatarSeconds } from 'utils/avatar'
import { convertAspectRatioToNumber } from 'utils/aspectRatio'
import {
  IResolvedVisualMediaPlacement,
  getAvailableDurationOnRow
} from 'utils/visualMediaPlacement'

// Free users can't render premium qualities. If `quality` is premium (e.g.
// loaded from a clip generated while the user was subscribed, or a duplicated/
// template project), drop to the best available non-premium quality from the
// model's `possibleQualities`; otherwise leave it untouched. Used to enforce the
// quality gate at the generate boundary, not just in the dropdown UI.
export const clampQualityForFreeUser = (
  quality: number,
  possibleQualities: number[],
  isFreeUser: boolean
): number => {
  if (!isFreeUser || quality < MIN_PREMIUM_QUALITY) {
    return quality
  }
  const freeQualities = possibleQualities.filter(q => q < MIN_PREMIUM_QUALITY)
  return freeQualities.length ? Math.max(...freeQualities) : quality
}

/**
 * Builds a FormData payload for the file-upload endpoint by appending each
 * item's file under the `file` field (the shape `uploadFile_API` expects).
 */
export const buildUploadFormData = (
  uploadItems: IUploadMediaItem[]
): FormData => {
  const formData = new FormData()
  uploadItems.forEach(item => formData.append('file', item.file))
  return formData
}

// Image fields on IPublicAvatar (all strings) — narrowed so the lookup below
// stays type-safe without casts.
type TAvatarImageField =
  | 'url_16_9'
  | 'url_1_1'
  | 'url_9_16'
  | 'thumbnail_16_9'
  | 'thumbnail_1_1'
  | 'thumbnail_9_16'

const ASPECT_RATIO_ASSET_FIELDS: Record<
  string,
  { url: TAvatarImageField; thumbnail: TAvatarImageField }
> = {
  '16:9': { url: 'url_16_9', thumbnail: 'thumbnail_16_9' },
  '1:1': { url: 'url_1_1', thumbnail: 'thumbnail_1_1' },
  '9:16': { url: 'url_9_16', thumbnail: 'thumbnail_9_16' }
}

// Default to the 16:9 variant for any unrecognized ratio.
const DEFAULT_ASSET_FIELDS = ASPECT_RATIO_ASSET_FIELDS['16:9']

// Resolves the effective avatar aspect ratio: the per-avatar override when set,
// otherwise the project's aspect ratio. Centralizes the "null = follow project"
// fallback shared by the public-avatars panel and the generate thunk.
export const resolveAvatarAspectRatio = (
  override: string | null,
  projectAspectRatio: string
): string => override ?? projectAspectRatio

// Avatars carry no intrinsic pixel size before render, so we derive a
// representative media rect from the aspect-ratio string for FIT math
// (`calculateMediaRect` / `resolveCompletedMediaRect`). Only the aspect matters —
// the absolute scale is irrelevant — so a fixed-height fake rect suffices. Shared
// by the placeholder builder (fresh gen) and the regenerate flow.
const FAKE_AVATAR_HEIGHT = 100
export const avatarAspectRatioToFakeDimensions = (
  aspectRatio: string
): IDimensions => ({
  width: FAKE_AVATAR_HEIGHT * convertAspectRatioToNumber(aspectRatio),
  height: FAKE_AVATAR_HEIGHT
})

// The media size to FIT the loading placeholder against: the source image's
// natural pixel size when known (uploaded / My-Files avatars, so the loader
// shows the photo at its real aspect ratio instead of stretching it), otherwise
// a fake rect derived from the effective aspect ratio (public avatars, which
// carry no pixel size — only the selected ratio).
export const resolveAvatarMediaDimensions = (
  avatar: IAvatarData | null,
  effectiveAspectRatio: string
): IDimensions =>
  avatar?.width && avatar?.height
    ? { width: avatar.width, height: avatar.height }
    : avatarAspectRatioToFakeDimensions(effectiveAspectRatio)

// Loads an image's natural pixel size from its URL. Resolves null on load
// failure — or after a timeout, so a request that neither loads nor errors
// (stalled socket) can't leave the awaiting form-load pending forever. (Only
// reading naturalWidth/Height, so no CORS/canvas-taint concern for cross-origin
// CDN/S3 images.)
const IMAGE_DIMENSIONS_LOAD_TIMEOUT_MS = 5000
const loadImageNaturalDimensions = (url: string): Promise<IDimensions | null> =>
  new Promise(resolve => {
    const img = new Image()
    const timeoutId = setTimeout(
      () => resolve(null),
      IMAGE_DIMENSIONS_LOAD_TIMEOUT_MS
    )
    img.onload = () => {
      clearTimeout(timeoutId)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      clearTimeout(timeoutId)
      resolve(null)
    }
    img.src = url
  })

// Recovers the reference image's REAL pixel size for an avatar project loaded
// into the regenerate / history-prefill form. The saved project doesn't carry
// the uploaded image's dimensions on the `avatar` object, and without them the
// loading placeholder FITs against the canvas aspect ratio — stretching a
// photo whose ratio differs from the canvas (the "fill instead of fit" regen
// bug). Prefers the dimensions echoed on the project's reference image, else
// loads them from the image URL. Returns undefined when neither is available
// (public avatars carry no pixel size / a load failure) so the form falls back
// to the aspect-ratio fake rect — the pre-existing, correct behavior for those.
export const resolveAvatarReferenceDimensions = async (
  data: IAIVideoProject
): Promise<IDimensions | undefined> => {
  const responseDims = data.referenceMedias.images?.[0]?.dimensions
  if (responseDims?.width && responseDims?.height) {
    return responseDims
  }
  if (!data.imageUrl) {
    return undefined
  }
  return (await loadImageNaturalDimensions(data.imageUrl)) ?? undefined
}

/**
 * Picks the public avatar's full url + grid thumbnail for the given aspect
 * ratio.
 */
export const getAvatarAssetsForAspectRatio = (
  avatar: IPublicAvatar,
  aspectRatio: string
): { url: string; thumbnail: string } => {
  const fields = ASPECT_RATIO_ASSET_FIELDS[aspectRatio] ?? DEFAULT_ASSET_FIELDS
  return {
    url: avatar[fields.url],
    thumbnail: avatar[fields.thumbnail]
  }
}

interface IBuildAvatarPlaceholderParams {
  id: string // = aiAvatarVideoHash
  assetId: string // = aiAvatarProjectId
  defaultDimensions: IDimensions
  // Current timeline items — used to compute the fallback end-of-timeline
  // startTime when no explicit placement is given.
  mediaItems: IMediaItem[]
  // Resolved playhead placement (startTime + zIndex). Falls back to the end of
  // the timeline on line 0 when omitted.
  placement?: IResolvedVisualMediaPlacement
  // Optional avatar still — only used as the timeline thumbnail / loader image.
  previewImageUrl?: string
  // The speech text being generated — used to estimate the placeholder duration
  // (chars→seconds) so the loading clip is roughly the real length, instead of a
  // fixed 5s that jumps when the socket swaps in the real samples.
  speechText: string
  // The avatar's own media size (`resolveAvatarMediaDimensions`): the source
  // image's natural pixels for uploaded/My-Files avatars, else a fake rect from
  // the effective aspect ratio for public avatars. The loading clip is
  // FIT-centered into the canvas against this so its shape matches the source
  // image (no stretch) and the rendered samples (B10).
  mediaDimensions: IDimensions
}

export const buildAvatarPlaceholderMediaItem = ({
  id,
  assetId,
  defaultDimensions,
  mediaItems,
  placement,
  previewImageUrl,
  speechText,
  mediaDimensions
}: IBuildAvatarPlaceholderParams): IMediaItem => {
  const startTime = placement?.startTime ?? getLastMediaItemEndTime(mediaItems)
  const zIndex = placement?.zIndex ?? 0
  // Estimate the clip length from the speech text, floored at DEFAULT_MEDIA_DURATION
  // so blank/very-short text doesn't produce a sub-second sliver on the timeline.
  const estimatedDuration = Math.max(
    estimateAvatarSeconds(speechText),
    DEFAULT_MEDIA_DURATION
  )

  // The playhead placement only guarantees DEFAULT_MEDIA_DURATION of clear space
  // on this row, but the estimate can be longer — clamp the loading clip to the
  // gap before the next clip so it doesn't overlap a right-neighbor while it
  // generates. The real samples (longer) cascade neighbors via
  // spliceSamplesIntoPendingPlaceholder, so this only affects the loading view.
  //
  // This never makes a sub-DEFAULT_MEDIA_DURATION sliver: `estimatedDuration` is
  // already floored at DEFAULT_MEDIA_DURATION above, and `availableDuration` is
  // either Infinity (no right-neighbor) or >= DEFAULT_MEDIA_DURATION (placement
  // only reuses a row when the first DEFAULT_MEDIA_DURATION after the playhead is
  // clear — see resolveVisualMediaPlacement). So min(...) is always
  // >= DEFAULT_MEDIA_DURATION whenever there's room.
  const duration = Math.min(
    estimatedDuration,
    getAvailableDurationOnRow(mediaItems, zIndex, startTime)
  )

  // FIT the avatar's media size into the project canvas (mirrors aiImage's
  // `createNewMediaItem` / aiVideo's reference-image FIT) and center it. The box
  // then matches the source image's aspect ratio, so the loader (which stretches
  // the preview to the box) shows it undistorted — and it matches the rendered
  // samples' shape, so there's no jump when the socket swaps them in.
  const mediaRect = calculateMediaRect({
    mediaDimensions,
    mode: FIT,
    canvasDimensions: defaultDimensions,
    rotation: 0
  })

  return {
    id,
    assetId,
    type: PROJECT_MEDIA_TYPES.aiAvatar,
    width: mediaRect.width,
    height: mediaRect.height,
    rotation: 0,
    duration,
    startTime,
    zIndex,
    coordinates: { x: mediaRect.x, y: mediaRect.y },
    meta: {
      path: previewImageUrl ?? null,
      status: MediaStatus.PENDING,
      originalHeight: mediaDimensions.height,
      originalWidth: mediaDimensions.width,
      originalDuration: duration,
      trimStart: 0
    }
  }
}
